import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { NAV_LINKS, PHONE, PHONE_LINK } from "@/data/site";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open
          ? "border-b border-white/10 bg-black/70 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 md:px-10">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <img
            src="/logo-header.png"
            alt="Sage Auto Group"
            className="h-10 w-auto transition-opacity duration-300 hover:opacity-80 md:h-11"
          />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 ${
                  isActive ? "text-gold" : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-6 lg:flex">
          <a
            href={PHONE_LINK}
            data-testid="nav-phone"
            className="flex items-center gap-2 text-sm font-semibold tracking-wider text-white transition-colors duration-300 hover:text-gold"
          >
            <Phone className="h-4 w-4 text-gold" strokeWidth={1.5} />
            {PHONE}
          </a>
          <Link
            to="/contact"
            data-testid="nav-request-assistance"
            className="rounded-full bg-gold px-6 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-obsidian transition-[background-color,transform] duration-300 hover:bg-gold-hover hover:-translate-y-0.5"
          >
            Request Assistance
          </Link>
        </div>

        <button
          data-testid="nav-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {NAV_LINKS.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                >
                  <NavLink
                    to={link.to}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block py-3 font-serif text-2xl ${isActive ? "text-gold" : "text-white"}`
                    }
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <a
                href={PHONE_LINK}
                data-testid="mobile-nav-phone"
                className="mt-4 flex items-center justify-center gap-3 rounded-full bg-gold py-4 text-sm font-bold uppercase tracking-[0.2em] text-obsidian"
              >
                <Phone className="h-4 w-4" strokeWidth={2} />
                Call {PHONE}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
